/**
 * Segmind Virtual Try-On Service
 *
 * Calls the backend Cloud Function to process virtual try-on.
 * The API key is kept server-side only for security.
 */

import { apiCall } from '../api/config';
import { getCaptchaToken } from './turnstile';

const RATE_LIMIT_PER_HOUR = parseInt(process.env.REACT_APP_RATE_LIMIT_TRYON_PER_HOUR) || 15;
const RATE_LIMIT_STORAGE_KEY = 'segmind_rate_limit';

/**
 * Rate limiting - Track try-on requests
 */
const checkRateLimit = () => {
  const now = Date.now();
  const rateLimitData = JSON.parse(localStorage.getItem(RATE_LIMIT_STORAGE_KEY) || '[]');

  // Filter requests from last hour
  const oneHourAgo = now - (60 * 60 * 1000);
  const recentRequests = rateLimitData.filter(timestamp => timestamp > oneHourAgo);

  // Check if limit exceeded
  if (recentRequests.length >= RATE_LIMIT_PER_HOUR) {
    const oldestRequest = Math.min(...recentRequests);
    const resetTime = new Date(oldestRequest + 60 * 60 * 1000);
    throw new Error(
      `Limite di ${RATE_LIMIT_PER_HOUR} prove virtuali orarie raggiunto. Riprova alle ${resetTime.toLocaleTimeString()}`
    );
  }

  // Add current request
  recentRequests.push(now);
  localStorage.setItem(RATE_LIMIT_STORAGE_KEY, JSON.stringify(recentRequests));

  return recentRequests.length;
};

/**
 * Get remaining tries for current hour
 */
export const getRemainingTries = () => {
  const rateLimitData = JSON.parse(localStorage.getItem(RATE_LIMIT_STORAGE_KEY) || '[]');
  const oneHourAgo = Date.now() - (60 * 60 * 1000);
  const recentRequests = rateLimitData.filter(timestamp => timestamp > oneHourAgo);
  return RATE_LIMIT_PER_HOUR - recentRequests.length;
};

/**
 * Determine garment category from product
 */
const getGarmentCategory = (product) => {
  const name = (product?.name || '').toLowerCase();

  if (name.includes('pantalone') || name.includes('jeans') || name.includes('gonna')) {
    return 'lower';
  } else if (name.includes('vestito') || name.includes('abito') || name.includes('tuta')) {
    return 'full';
  } else {
    return 'upper';
  }
};

/**
 * Call backend Cloud Function for virtual try-on
 *
 * @param {string} userPhotoBase64 - User photo in base64
 * @param {object} product - Product object with imageUrl and id
 * @returns {Promise<string>} - Try-on result image URL or base64
 */
export const performVirtualTryOn = async (userPhotoBase64, product, email, sessionId) => {
  try {
    // Check rate limit first
    const requestsUsed = checkRateLimit();
    console.log(`API calls used: ${requestsUsed}/${RATE_LIMIT_PER_HOUR} this hour`);

    const category = getGarmentCategory(product);

    // Get captcha token (null if not configured — server skips verification in dev)
    const captchaToken = await getCaptchaToken();

    // Call the backend Cloud Function
    const body = {
      userPhotoUrl: userPhotoBase64,
      productId: product.id,
      garmentImageUrl: product.imageUrl || product.immagine_url,
      category: category,
      productName: product.name,
      productDetails: {
        brand: product.brand,
        price: product.price,
        name: product.name,
        url_originale: product.url_originale
      },
      captchaToken
    };
    if (email) body.email = email;
    if (sessionId) body.sessionId = sessionId;

    const result = await apiCall('/try-on', {
      method: 'POST',
      body: JSON.stringify(body)
    });

    if (result.success && result.resultUrl) {
      // Return full result object so caller can check emailSent
      return result;
    }

    // If demo mode, return user photo as fallback
    if (result.demo) {
      console.warn('Try-on in demo mode: API key not configured on server');
      return userPhotoBase64;
    }

    throw new Error(result.error || 'Try-on failed');

  } catch (error) {
    console.error('Virtual Try-On Error:', error);
    throw error;
  }
};

/**
 * Estimate API cost for a try-on
 */
export const estimateCost = () => {
  return 0.015;
};

/**
 * Check if API is configured (asks backend)
 */
export const isApiConfigured = () => {
  // Always true since the API key is server-side
  return true;
};

/**
 * Get API status from backend
 */
export const getApiStatus = async () => {
  try {
    await apiCall('/stats');
    return {
      status: 'active',
      message: 'Backend connected'
    };
  } catch (error) {
    return {
      status: 'error',
      message: `Connection failed: ${error.message}`
    };
  }
};

const segmindService = {
  performVirtualTryOn,
  estimateCost,
  isApiConfigured,
  getApiStatus,
  getRemainingTries
};

export default segmindService;
